import React, { FormEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styles from './AdminUsers.module.scss';
import {
    AdminRoleDto,
    AdminUserDto,
    LegacySubscriptionLinkPreviewDto,
    UserSubscriptionSummaryDto
} from '../../../types/admin.types';
import { AnalyticsUserWatchHistoryResponse } from '../../../types/analytics.types';
import {
    AdminUserMutationError,
    cancelAdminUserSubscription,
    disableAdminUser,
    enableAdminUser,
    fetchAdminRoles,
    fetchAdminUser,
    fetchAdminUserSubscriptionSummary,
    linkLegacySubscription,
    previewLegacySubscriptionLink,
    replaceAdminUserRoles,
    sendAdminUserPasswordResetEmail,
    syncAdminUserSubscription,
    updateAdminUser
} from '../../../services/admin.service';
import { useAnalyticsService } from '../../../services/analytics.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import {
    ADMIN_USER_ACCESS_ROLES,
    AdminUserAccessRole,
    buildAdminUserRolesForAccess,
    getAdminUserAccessRole,
    isAdminUserAccessRole,
    isSuperAdmin,
    ROLE_ADMINISTRATOR,
    ROLE_CONTENT_PROCESSOR_SERVICE,
    ROLE_MEMBER,
    ROLE_SUPER_ADMINISTRATOR
} from '../../../utils/roles';
import { Routes } from '../../../types/constants';
import { useAdminUnsavedChanges } from './useAdminUnsavedChanges';

type ConfirmationKind = 'reset' | 'cancelSubscription' | 'disable' | 'elevateRoles' | 'linkLegacySubscription';

interface LocationState {
    actionMessage?: string;
    usersReturnTo?: string;
}

const protectedRoleNames = new Set([ROLE_SUPER_ADMINISTRATOR, ROLE_CONTENT_PROCESSOR_SERVICE]);
const elevatedRoleNames = new Set([ROLE_ADMINISTRATOR, ROLE_SUPER_ADMINISTRATOR, ROLE_CONTENT_PROCESSOR_SERVICE]);
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const stripeSubscriptionIdPattern = /^sub_[A-Za-z0-9_-]+$/;

const hasProtectedRole = (roles: string[]) => roles.some((role) => protectedRoleNames.has(role));
const sameUserId = (left?: string | null, right?: string | null) => (
    Boolean(left && right && left.toLowerCase() === right.toLowerCase())
);

const formatDateTime = (value?: string | null) => {
    if (!value) return '-';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const formatDate = (value?: string | null) => {
    if (!value) return '-';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
};

const subscriptionPresentation = (summary: UserSubscriptionSummaryDto | null) => {
    const status = summary?.planStatus?.toLowerCase();
    if (!status) return { label: 'No subscription', className: styles.statusNeutral };
    if (status === 'canceled' || status === 'incomplete_expired') {
        return { label: 'Canceled', className: styles.statusNeutral };
    }
    if (summary?.cancelAtPeriodEnd) return { label: 'Canceling', className: styles.statusWarning };
    if (status === 'active' || status === 'trialing') {
        return { label: status === 'trialing' ? 'Trialing' : 'Active', className: styles.statusSuccess };
    }
    return { label: status.replace(/_/g, ' '), className: styles.statusWarning };
};

interface ConfirmationDialogProps {
    title: string;
    body: string;
    confirmLabel: string;
    danger: boolean;
    busy: boolean;
    action: () => void | Promise<void>;
    onClose: () => void;
}

const ConfirmationDialog = ({
    title,
    body,
    confirmLabel,
    danger,
    busy,
    action,
    onClose
}: ConfirmationDialogProps) => {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const confirmButtonRef = useRef<HTMLButtonElement>(null);
    const titleId = useId();
    const bodyId = useId();

    useEffect(() => {
        const dialog = dialogRef.current;
        const previouslyFocusedElement = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        if (!dialog) return undefined;

        if (typeof dialog.showModal === 'function') {
            dialog.showModal();
        } else {
            dialog.setAttribute('open', '');
        }
        confirmButtonRef.current?.focus();

        return () => {
            if (dialog.open && typeof dialog.close === 'function') {
                dialog.close();
            } else {
                dialog.removeAttribute('open');
            }
            previouslyFocusedElement?.focus();
        };
    }, [onClose]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDialogElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            if (!busy) onClose();
            return;
        }
        if (event.key !== 'Tab') return;

        const focusableElements = Array.from(
            event.currentTarget.querySelectorAll<HTMLElement>('button:not([disabled])')
        );
        if (focusableElements.length === 0) {
            event.preventDefault();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (event.shiftKey && (document.activeElement === firstElement || document.activeElement === event.currentTarget)) {
            event.preventDefault();
            lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }
    };

    return (
        <dialog
            className={styles.modal}
            ref={dialogRef}
            role="alertdialog"
            aria-labelledby={titleId}
            aria-describedby={bodyId}
            onCancel={(event) => {
                event.preventDefault();
                if (!busy) onClose();
            }}
            onKeyDown={handleKeyDown}
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !busy) onClose();
            }}
        >
            <div className={styles.modalContent}>
                <h2 id={titleId}>{title}</h2>
                <p id={bodyId}>{body}</p>
                <div className={styles.formActions}>
                    <button
                        ref={confirmButtonRef}
                        className={danger ? styles.dangerButton : styles.primaryButton}
                        type="button"
                        disabled={busy}
                        onClick={() => void action()}
                    >
                        {confirmLabel}
                    </button>
                    <button className={styles.secondaryButton} type="button" disabled={busy} onClick={onClose}>
                        Keep unchanged
                    </button>
                </div>
            </div>
        </dialog>
    );
};

const AdminUserDetail = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = (location.state ?? {}) as LocationState;
    const { getUserInfo } = useUserState();
    const currentUser = getUserInfo();
    const token = currentUser?.token;
    const canManageProtectedRoles = isSuperAdmin(currentUser?.roles);
    const { fetchUserWatchHistory } = useAnalyticsService();

    const [user, setUser] = useState<AdminUserDto | null>(null);
    const [draft, setDraft] = useState<AdminUserDto | null>(null);
    const [roles, setRoles] = useState<AdminRoleDto[]>([]);
    const [selectedAccessRole, setSelectedAccessRole] = useState<AdminUserAccessRole>(ROLE_MEMBER);
    const [subscription, setSubscription] = useState<UserSubscriptionSummaryDto | null>(null);
    const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);
    const [legacySubscriptionId, setLegacySubscriptionId] = useState('');
    const [legacyLinkPreview, setLegacyLinkPreview] = useState<LegacySubscriptionLinkPreviewDto | null>(null);
    const [activity, setActivity] = useState<AnalyticsUserWatchHistoryResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [activeOperation, setActiveOperation] = useState<string | null>(null);
    const [confirmation, setConfirmation] = useState<ConfirmationKind | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [actionMessage, setActionMessage] = useState<string | null>(locationState.actionMessage ?? null);
    const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
    const [activityError, setActivityError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const routeUserIdRef = useRef(userId);
    routeUserIdRef.current = userId;

    const desiredRoles = user ? buildAdminUserRolesForAccess(user.roles, selectedAccessRole) : [];
    const rolesDirty = user ? selectedAccessRole !== getAdminUserAccessRole(user.roles) : false;
    const profileDirty = Boolean(user && draft && (
        user.username !== draft.username
        || user.firstName !== draft.firstName
        || user.lastName !== draft.lastName
        || user.email !== draft.email
    ));
    useAdminUnsavedChanges((profileDirty || rolesDirty) && activeOperation === null);

    const loadSubscription = useCallback(async (targetUserId: string) => {
        setIsSubscriptionLoading(true);
        setSubscriptionError(null);
        try {
            const response = await fetchAdminUserSubscriptionSummary(targetUserId, token);
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            if (!sameUserId(response.userId, targetUserId)) {
                setSubscription(null);
                setSubscriptionError('Subscription details did not match the requested user.');
                return;
            }
            setSubscription(response);
        } catch {
            if (sameUserId(routeUserIdRef.current, targetUserId)) {
                setSubscription(null);
                setSubscriptionError('Subscription details are temporarily unavailable.');
            }
        } finally {
            if (sameUserId(routeUserIdRef.current, targetUserId)) {
                setIsSubscriptionLoading(false);
            }
        }
    }, [token]);

    useEffect(() => {
        let active = true;
        if (!userId) {
            setErrorMessage('User was not provided.');
            setIsLoading(false);
            return undefined;
        }

        setIsLoading(true);
        setUser(null);
        setDraft(null);
        setRoles([]);
        setSubscription(null);
        setIsSubscriptionLoading(true);
        setLegacySubscriptionId('');
        setLegacyLinkPreview(null);
        setActivity(null);
        setSubscriptionError(null);
        setActivityError(null);
        setErrorMessage(null);
        setActionMessage(locationState.actionMessage ?? null);
        setFieldErrors({});
        setIsEditing(false);
        setActiveOperation(null);
        setConfirmation(null);
        Promise.all([fetchAdminUser(userId, token), fetchAdminRoles(token)])
            .then(([loadedUser, loadedRoles]) => {
                if (!active || !sameUserId(routeUserIdRef.current, userId)) return;
                if (!sameUserId(loadedUser.userId, userId)) {
                    setErrorMessage('Unable to load the requested user.');
                    return;
                }
                const normalizedRoles = loadedRoles
                    .map((role: any) => ({ id: role.id ?? role.Id ?? '', name: role.name ?? role.Name ?? '' }))
                    .filter((role) => role.name.trim().length > 0)
                    .sort((left, right) => left.name.localeCompare(right.name));
                setUser(loadedUser);
                setDraft({ ...loadedUser });
                setSelectedAccessRole(getAdminUserAccessRole(loadedUser.roles));
                setRoles(normalizedRoles);
                void loadSubscription(loadedUser.userId);
            })
            .catch((error) => {
                if (active && sameUserId(routeUserIdRef.current, userId)) {
                    setErrorMessage(error instanceof Error ? error.message : 'Unable to load user.');
                }
            })
            .finally(() => {
                if (active && sameUserId(routeUserIdRef.current, userId)) setIsLoading(false);
            });
        return () => {
            active = false;
        };
    }, [loadSubscription, locationState.actionMessage, token, userId]);

    useEffect(() => {
        let active = true;
        const targetUserId = user?.userId;
        if (!targetUserId || !sameUserId(routeUserIdRef.current, targetUserId)) return undefined;
        setActivity(null);
        setActivityError(null);
        fetchUserWatchHistory(targetUserId, {
            days: 90,
            page: 1,
            pageSize: 1,
            status: 'all',
            search: '',
            sort: 'lastWatchedAt_desc'
        })
            .then((response) => {
                if (active
                    && sameUserId(routeUserIdRef.current, targetUserId)
                    && sameUserId(response.userId, targetUserId)) {
                    setActivity(response);
                }
            })
            .catch(() => {
                if (active && sameUserId(routeUserIdRef.current, targetUserId)) {
                    setActivityError('Viewing activity is temporarily unavailable.');
                }
            });
        return () => {
            active = false;
        };
    }, [fetchUserWatchHistory, user?.userId]);

    const targetIsProtected = Boolean(user && hasProtectedRole(user.roles));
    const canManageTarget = Boolean(user?.userId && (canManageProtectedRoles || !targetIsProtected));
    const isCurrentUser = sameUserId(currentUser?.userId, user?.userId);
    const canEditTargetUsername = Boolean(user && !isCurrentUser && user.canEditUsername !== false);
    const assignableRoles = useMemo(() => {
        const available = roles
            .filter((role) => isAdminUserAccessRole(role.name))
            .filter((role) => canManageProtectedRoles || !protectedRoleNames.has(role.name));
        if (!available.some((role) => role.name === ROLE_MEMBER)) {
            available.push({ id: ROLE_MEMBER, name: ROLE_MEMBER });
        }
        return available.sort((left, right) => (
            ADMIN_USER_ACCESS_ROLES.indexOf(left.name as AdminUserAccessRole)
            - ADMIN_USER_ACCESS_ROLES.indexOf(right.name as AdminUserAccessRole)
        ));
    }, [canManageProtectedRoles, roles]);
    const subscriptionState = subscriptionPresentation(subscription);
    const subscriptionAccessNotice = subscription?.cancelAtPeriodEnd
        && ['active', 'trialing'].includes(subscription.planStatus?.toLowerCase() ?? '')
        ? subscription.planCurrentPeriodEnd
            ? `Subscriber access remains active until ${formatDate(subscription.planCurrentPeriodEnd)}. No role change is required.`
            : 'Subscriber access remains active until the current billing period ends. No role change is required.'
        : null;
    const canCancelSubscription = Boolean(
        subscription?.planStatus
        && !subscription.cancelAtPeriodEnd
        && !['error', 'canceled', 'incomplete_expired'].includes(subscription.planStatus.toLowerCase())
    );
    const canLinkLegacySubscription = Boolean(
        canManageProtectedRoles
        && canManageTarget
        && !user?.stripeCustomerId
        && !isSubscriptionLoading
        && !subscriptionError
        && !subscription?.planStatus
    );
    const closeConfirmation = useCallback(() => setConfirmation(null), []);

    const returnToUsers = () => navigate(locationState.usersReturnTo ?? Routes.ADMIN_USERS);
    const openWatchHistory = () => navigate(
        `${Routes.ADMIN_USERS}/${encodeURIComponent(user?.userId ?? '')}/watch-history`,
        { state: locationState }
    );

    const validateProfile = () => {
        const nextErrors: Record<string, string> = {};
        if (!draft?.username.trim()) nextErrors.username = 'Username is required.';
        if (!draft?.email.trim()) nextErrors.email = 'Email is required.';
        else if (!emailPattern.test(draft.email.trim())) nextErrors.email = 'Enter a valid email address.';
        setFieldErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSaveProfile = async (event: FormEvent) => {
        event.preventDefault();
        if (!user?.userId || !draft || !validateProfile()) return;
        const targetUserId = user.userId;
        setActiveOperation('profile');
        setErrorMessage(null);
        setActionMessage(null);
        try {
            const updated = await updateAdminUser({
                ...user,
                username: isCurrentUser || user.canEditUsername === false ? user.username : draft.username.trim(),
                firstName: draft.firstName.trim(),
                lastName: draft.lastName.trim(),
                email: draft.email.trim()
            }, token);
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            const merged = {
                ...user,
                ...updated,
                roles: user.roles,
                username: updated.username || (
                    isCurrentUser || user.canEditUsername === false ? user.username : draft.username.trim()
                ),
                firstName: draft.firstName.trim(),
                lastName: draft.lastName.trim(),
                email: draft.email.trim()
            };
            setUser(merged);
            setDraft({ ...merged });
            setIsEditing(false);
            setActionMessage('Profile details saved.');
        } catch (error) {
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            if (error instanceof AdminUserMutationError) {
                setErrorMessage(error.message);
                setFieldErrors(error.fieldErrors);
            } else {
                setErrorMessage(error instanceof Error ? error.message : 'Unable to save profile details.');
            }
        } finally {
            if (sameUserId(routeUserIdRef.current, targetUserId)) setActiveOperation(null);
        }
    };

    const persistRoles = async () => {
        if (!user?.userId) return;
        const targetUserId = user.userId;
        setConfirmation(null);
        setActiveOperation('roles');
        setErrorMessage(null);
        setActionMessage(null);
        try {
            const updatedRoles = await replaceAdminUserRoles({ userId: targetUserId, roles: desiredRoles }, token);
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            const updatedUser = { ...user, roles: updatedRoles };
            setUser(updatedUser);
            setDraft((current) => current ? { ...current, roles: updatedRoles } : current);
            setSelectedAccessRole(getAdminUserAccessRole(updatedRoles));
            setActionMessage('Access level saved.');
        } catch (error) {
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            setErrorMessage(error instanceof Error ? error.message : 'Unable to save access level.');
        } finally {
            if (sameUserId(routeUserIdRef.current, targetUserId)) setActiveOperation(null);
        }
    };

    const handleSaveRoles = () => {
        if (!user?.userId) return;
        const addsElevatedRole = elevatedRoleNames.has(selectedAccessRole) && !user.roles.includes(selectedAccessRole);
        if (addsElevatedRole) {
            setConfirmation('elevateRoles');
            return;
        }
        void persistRoles();
    };

    const handleResetPassword = async () => {
        if (!user?.userId) return;
        const targetUserId = user.userId;
        setConfirmation(null);
        setActiveOperation('reset');
        setErrorMessage(null);
        setActionMessage(null);
        try {
            await sendAdminUserPasswordResetEmail(targetUserId, token);
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            const updated = { ...user, requiresPasswordReset: true };
            setUser(updated);
            setDraft((current) => current ? { ...current, requiresPasswordReset: true } : current);
            setActionMessage(`Password reset email sent to ${user.email}.`);
        } catch (error) {
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            setErrorMessage(error instanceof Error ? error.message : 'Unable to send password reset email.');
        } finally {
            if (sameUserId(routeUserIdRef.current, targetUserId)) setActiveOperation(null);
        }
    };

    const handleToggleAccount = async (shouldEnable: boolean) => {
        if (!user?.userId) return;
        const targetUserId = user.userId;
        setConfirmation(null);
        setActiveOperation(shouldEnable ? 'enable' : 'disable');
        setErrorMessage(null);
        setActionMessage(null);
        try {
            if (shouldEnable) {
                await enableAdminUser(targetUserId, token);
            } else {
                await disableAdminUser(targetUserId, token);
            }
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            const updated = { ...user, enabled: shouldEnable };
            setUser(updated);
            setDraft((current) => current ? { ...current, enabled: shouldEnable } : current);
            setActionMessage(`Account ${shouldEnable ? 'enabled' : 'disabled'}.`);
        } catch (error) {
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            setErrorMessage(error instanceof Error ? error.message : `Unable to ${shouldEnable ? 'enable' : 'disable'} account.`);
        } finally {
            if (sameUserId(routeUserIdRef.current, targetUserId)) setActiveOperation(null);
        }
    };

    const handleSyncSubscription = async () => {
        if (!user?.userId) return;
        const targetUserId = user.userId;
        setActiveOperation('sync');
        setErrorMessage(null);
        setActionMessage(null);
        setSubscriptionError(null);
        try {
            const response = await syncAdminUserSubscription(targetUserId, token);
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            if (!sameUserId(response.userId, targetUserId)) {
                setSubscription(null);
                setSubscriptionError('Subscription details did not match the requested user.');
                return;
            }
            setSubscription(response);
            setActionMessage('Subscription synchronized from Stripe.');
        } catch (error) {
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            setErrorMessage(error instanceof Error ? error.message : 'Unable to synchronize subscription.');
        } finally {
            if (sameUserId(routeUserIdRef.current, targetUserId)) setActiveOperation(null);
        }
    };

    const handleCancelSubscription = async () => {
        if (!user?.userId) return;
        const targetUserId = user.userId;
        setConfirmation(null);
        setActiveOperation('cancelSubscription');
        setErrorMessage(null);
        setActionMessage(null);
        try {
            await cancelAdminUserSubscription(targetUserId, token);
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            await loadSubscription(targetUserId);
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            setActionMessage('Subscription will cancel at the end of the current billing period.');
        } catch (error) {
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            setErrorMessage(error instanceof Error ? error.message : 'Unable to cancel subscription.');
        } finally {
            if (sameUserId(routeUserIdRef.current, targetUserId)) setActiveOperation(null);
        }
    };

    const handlePreviewLegacySubscription = async (event: FormEvent) => {
        event.preventDefault();
        if (!user?.userId) return;
        const targetUserId = user.userId;
        const stripeSubscriptionId = legacySubscriptionId.trim();
        setLegacyLinkPreview(null);
        setActionMessage(null);
        setErrorMessage(null);
        if (!stripeSubscriptionIdPattern.test(stripeSubscriptionId) || stripeSubscriptionId.length > 200) {
            setErrorMessage('Enter the exact Stripe subscription ID beginning with sub_.');
            return;
        }

        setActiveOperation('previewLegacySubscription');
        try {
            const preview = await previewLegacySubscriptionLink(targetUserId, stripeSubscriptionId, token);
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            if (!sameUserId(preview.userId, targetUserId)
                || preview.stripeSubscriptionId !== stripeSubscriptionId) {
                setErrorMessage('Stripe preview details did not match the requested user and subscription.');
                return;
            }
            setLegacyLinkPreview(preview);
        } catch (error) {
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            setErrorMessage(error instanceof Error ? error.message : 'Unable to preview the Stripe subscription.');
        } finally {
            if (sameUserId(routeUserIdRef.current, targetUserId)) setActiveOperation(null);
        }
    };

    const handleLinkLegacySubscription = async () => {
        if (!user?.userId || !legacyLinkPreview) return;
        const targetUserId = user.userId;
        const preview = legacyLinkPreview;
        setConfirmation(null);
        setActiveOperation('linkLegacySubscription');
        setActionMessage(null);
        setErrorMessage(null);
        try {
            await linkLegacySubscription(
                targetUserId,
                preview.stripeSubscriptionId,
                preview.stripeCustomerId,
                !preview.emailMatches,
                token
            );
            const [updatedUser, updatedSubscription] = await Promise.all([
                fetchAdminUser(targetUserId, token),
                fetchAdminUserSubscriptionSummary(targetUserId, token)
            ]);
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            if (!sameUserId(updatedUser.userId, targetUserId)
                || !sameUserId(updatedSubscription.userId, targetUserId)) {
                setErrorMessage('The linked subscription could not be refreshed for the requested user.');
                return;
            }
            setUser(updatedUser);
            setDraft({ ...updatedUser });
            setSelectedAccessRole(getAdminUserAccessRole(updatedUser.roles));
            setSubscription(updatedSubscription);
            setLegacySubscriptionId('');
            setLegacyLinkPreview(null);
            setActionMessage('Stripe subscription linked and Subscriber access reconciled.');
        } catch (error) {
            if (!sameUserId(routeUserIdRef.current, targetUserId)) return;
            setErrorMessage(error instanceof Error ? error.message : 'Unable to link the Stripe subscription.');
        } finally {
            if (sameUserId(routeUserIdRef.current, targetUserId)) setActiveOperation(null);
        }
    };

    const handleCopyStripeId = async () => {
        if (!user?.stripeCustomerId) return;
        try {
            await navigator.clipboard.writeText(user.stripeCustomerId);
            setActionMessage('Stripe customer ID copied.');
        } catch {
            setErrorMessage('Unable to copy Stripe customer ID.');
        }
    };

    const confirmationContent = (() => {
        if (!user || !confirmation) return null;
        if (confirmation === 'reset') {
            return {
                title: 'Send password reset email?',
                body: `A new reset link will be sent to ${user.email}, and ${user.username} must use it before signing in.`,
                confirmLabel: activeOperation === 'reset' ? 'Sending…' : 'Send reset email',
                danger: false,
                action: handleResetPassword
            };
        }
        if (confirmation === 'cancelSubscription') {
            return {
                title: 'Cancel subscription at period end?',
                body: `Billing for ${user.username} will stop at the end of the current period. This does not disable the account.`,
                confirmLabel: activeOperation === 'cancelSubscription' ? 'Canceling…' : 'Schedule cancellation',
                danger: true,
                action: handleCancelSubscription
            };
        }
        if (confirmation === 'disable') {
            return {
                title: 'Disable this account?',
                body: `${user.username} will be prevented from signing in. Their subscription is not canceled by this action.`,
                confirmLabel: activeOperation === 'disable' ? 'Disabling…' : 'Disable account',
                danger: true,
                action: () => handleToggleAccount(false)
            };
        }
        if (confirmation === 'linkLegacySubscription' && legacyLinkPreview) {
            const emailWarning = legacyLinkPreview.emailMatches
                ? `Stripe customer email matches ${user.email}.`
                : `Stripe customer email ${legacyLinkPreview.stripeCustomerEmail ?? 'is not set'} does not match ${user.email}.`;
            return {
                title: 'Link this Stripe subscription?',
                body: `${emailWarning} This will link customer ${legacyLinkPreview.stripeCustomerId}, import subscription ${legacyLinkPreview.stripeSubscriptionId}, and reconcile Subscriber access for ${user.username}.`,
                confirmLabel: activeOperation === 'linkLegacySubscription' ? 'Linking…' : 'Link subscription',
                danger: !legacyLinkPreview.emailMatches,
                action: handleLinkLegacySubscription
            };
        }
        return {
            title: 'Grant elevated access?',
            body: `The selected roles can expose administrative or service capabilities to ${user.username}. Confirm that this access is intentional.`,
            confirmLabel: activeOperation === 'roles' ? 'Saving…' : 'Grant access',
            danger: false,
            action: persistRoles
        };
    })();

    if (isLoading || (user && userId && !sameUserId(user.userId, userId))) {
        return <div className={styles.loadingState}>Loading user workspace…</div>;
    }

    if (!user || !draft) {
        return (
            <div className={styles.page}>
                <button className={styles.backButton} type="button" onClick={returnToUsers}>← Back to users</button>
                <div className={styles.errorBanner} role="alert">{errorMessage ?? 'User not found.'}</div>
            </div>
        );
    }

    const displayName = `${user.firstName} ${user.lastName}`.trim() || user.username;

    return (
        <div className={styles.page}>
            <header className={styles.workspaceHeader}>
                <div>
                    <button className={styles.backButton} type="button" onClick={returnToUsers}>← Back to users</button>
                    <div className={styles.userHero}>
                        <span className={styles.heroAvatar} aria-hidden="true">{displayName.slice(0, 1).toUpperCase()}</span>
                        <div>
                            <h1>{displayName}</h1>
                            <p>{user.username} · {user.email}</p>
                            <div className={styles.headerBadges}>
                                <span className={`${styles.statusBadge} ${user.enabled ? styles.statusSuccess : styles.statusDanger}`}>{user.enabled ? 'Enabled' : 'Disabled'}</span>
                                <span className={`${styles.statusBadge} ${subscriptionState.className}`}>{subscriptionState.label}</span>
                                <span className={`${styles.statusBadge} ${styles.statusNeutral}`}>{user.roles.length} {user.roles.length === 1 ? 'role' : 'roles'}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.sectionActions}>
                    {canManageTarget ? <button className={styles.primaryButton} type="button" onClick={() => setIsEditing(true)}>Edit profile</button> : null}
                    <details className={styles.rowMenu}>
                        <summary aria-label="More user actions">•••</summary>
                        <div>
                            <button type="button" onClick={openWatchHistory}>Watch history</button>
                            {canManageTarget ? <button type="button" onClick={() => setConfirmation('reset')}>Send password reset</button> : null}
                            {canManageTarget && user.enabled && !isCurrentUser ? <button type="button" onClick={() => setConfirmation('disable')}>Disable account</button> : null}
                        </div>
                    </details>
                </div>
            </header>

            {actionMessage ? <div className={styles.successBanner} role="status">{actionMessage}</div> : null}
            {errorMessage ? <div className={styles.errorBanner} role="alert">{errorMessage}</div> : null}
            {!canManageTarget ? <div className={styles.infoBanner}>This privileged account is visible for audit, but only a SuperAdministrator can modify it.</div> : null}
            {user.canEditUsername === false ? (
                <div className={styles.infoBanner}>
                    {user.usernameEditRestriction ?? 'This system account username cannot be changed.'}
                </div>
            ) : null}

            <div className={`${styles.workspaceGrid} ${styles.detailWorkspace}`}>
                <section className={styles.workspaceSection}>
                    <h2>Identity</h2>
                    <p className={styles.sectionLead}>Core account details. Usernames can change; the account ID remains stable.</p>
                    {isEditing ? (
                        <form onSubmit={handleSaveProfile} noValidate>
                            <div className={styles.formGrid}>
                                <label className={`${styles.formField} ${styles.formFieldFull}`}>
                                    Username
                                    <input
                                        value={draft.username}
                                        disabled={!canEditTargetUsername}
                                        onChange={(event) => {
                                            setDraft({ ...draft, username: event.target.value });
                                            setFieldErrors((current) => ({ ...current, username: '' }));
                                        }}
                                        maxLength={256}
                                        autoComplete="username"
                                        aria-invalid={Boolean(fieldErrors.username)}
                                    />
                                    {fieldErrors.username ? <span className={styles.fieldError}>{fieldErrors.username}</span> : null}
                                    {isCurrentUser ? (
                                        <span className={styles.fieldHelp}>Change your own username from your profile so your sign-in session refreshes safely.</span>
                                    ) : user.canEditUsername === false ? (
                                        <span className={styles.fieldHelp}>
                                            {user.usernameEditRestriction ?? 'This system account username cannot be changed.'}
                                        </span>
                                    ) : null}
                                </label>
                                <label className={styles.formField}>
                                    First name
                                    <input value={draft.firstName} onChange={(event) => { setDraft({ ...draft, firstName: event.target.value }); setFieldErrors((current) => ({ ...current, firstName: '' })); }} maxLength={256} aria-invalid={Boolean(fieldErrors.firstName)} />
                                    {fieldErrors.firstName ? <span className={styles.fieldError}>{fieldErrors.firstName}</span> : null}
                                </label>
                                <label className={styles.formField}>
                                    Last name
                                    <input value={draft.lastName} onChange={(event) => { setDraft({ ...draft, lastName: event.target.value }); setFieldErrors((current) => ({ ...current, lastName: '' })); }} maxLength={256} aria-invalid={Boolean(fieldErrors.lastName)} />
                                    {fieldErrors.lastName ? <span className={styles.fieldError}>{fieldErrors.lastName}</span> : null}
                                </label>
                                <label className={`${styles.formField} ${styles.formFieldFull}`}>
                                    Email
                                    <input type="email" value={draft.email} onChange={(event) => { setDraft({ ...draft, email: event.target.value }); setFieldErrors((current) => ({ ...current, email: '' })); }} maxLength={256} aria-invalid={Boolean(fieldErrors.email)} />
                                    {fieldErrors.email ? <span className={styles.fieldError}>{fieldErrors.email}</span> : null}
                                </label>
                            </div>
                            <div className={styles.formActions}>
                                <button className={styles.primaryButton} type="submit" disabled={activeOperation !== null || !profileDirty}>{activeOperation === 'profile' ? 'Saving…' : 'Save profile'}</button>
                                <button className={styles.secondaryButton} type="button" onClick={() => { setDraft({ ...user }); setFieldErrors({}); setIsEditing(false); }}>Cancel</button>
                            </div>
                        </form>
                    ) : (
                        <dl className={styles.factList}>
                            <div><dt>Username</dt><dd>{user.username}</dd></div>
                            <div><dt>Created</dt><dd>{formatDateTime(user.createdDate)}</dd></div>
                            <div><dt>First name</dt><dd>{user.firstName || 'Not provided'}</dd></div>
                            <div><dt>Last name</dt><dd>{user.lastName || 'Not provided'}</dd></div>
                            <div><dt>Email</dt><dd>{user.email}</dd></div>
                            <div><dt>Password</dt><dd>{user.requiresPasswordReset ? 'Reset required before login' : 'Reset complete'}</dd></div>
                        </dl>
                    )}
                </section>

                <section className={styles.workspaceSection}>
                    <h2>Roles and access</h2>
                    <p className={styles.sectionLead}>Member access and system-managed roles are retained automatically. Choose one account access level.</p>
                    {subscriptionAccessNotice ? <div className={styles.infoBanner}>{subscriptionAccessNotice}</div> : null}
                    {canManageTarget ? (
                        <>
                            <label className={styles.formField}>
                                Access level
                                <select
                                    value={selectedAccessRole}
                                    onChange={(event) => {
                                        if (isAdminUserAccessRole(event.target.value)) {
                                            setSelectedAccessRole(event.target.value);
                                        }
                                    }}
                                >
                                    {assignableRoles.map((role) => (
                                        <option
                                            key={role.id || role.name}
                                            value={role.name}
                                            disabled={isCurrentUser
                                                && role.name !== ROLE_ADMINISTRATOR
                                                && role.name !== ROLE_SUPER_ADMINISTRATOR}
                                        >
                                            {role.name}{protectedRoleNames.has(role.name) ? ' — protected' : ''}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <div className={styles.formActions}>
                                <button className={styles.primaryButton} type="button" disabled={!rolesDirty || activeOperation !== null} onClick={handleSaveRoles}>{activeOperation === 'roles' ? 'Saving…' : 'Save access'}</button>
                                <button className={styles.secondaryButton} type="button" disabled={!rolesDirty} onClick={() => setSelectedAccessRole(getAdminUserAccessRole(user.roles))}>Reset</button>
                            </div>
                            <div className={styles.actionItem}>
                                <div><strong>{user.enabled ? 'Account enabled' : 'Account disabled'}</strong><span>Disabling prevents login. It does not cancel or refund a subscription.</span></div>
                                {user.enabled ? (
                                    <button className={styles.dangerButton} type="button" disabled={activeOperation !== null || isCurrentUser} onClick={() => setConfirmation('disable')}>{isCurrentUser ? 'Current account' : 'Disable account'}</button>
                                ) : (
                                    <button className={styles.secondaryButton} type="button" disabled={activeOperation !== null} onClick={() => void handleToggleAccount(true)}>{activeOperation === 'enable' ? 'Enabling…' : 'Enable account'}</button>
                                )}
                            </div>
                        </>
                    ) : <div className={styles.roleList}>{user.roles.map((role) => <span key={role}>{role}</span>)}</div>}
                </section>

                <section className={styles.workspaceSection}>
                    <h2>Subscription</h2>
                    <p className={styles.sectionLead}>Local subscription state used for access decisions, with deliberate Stripe support actions.</p>
                    {subscriptionError ? <div className={styles.errorBanner}>{subscriptionError}</div> : (
                        <dl className={styles.factList}>
                            <div><dt>Status</dt><dd><span className={`${styles.statusBadge} ${subscriptionState.className}`}>{subscriptionState.label}</span></dd></div>
                            <div><dt>Interval</dt><dd>{subscription?.planInterval ?? '-'}</dd></div>
                            <div><dt>Renews / ends</dt><dd>{formatDate(subscription?.planCurrentPeriodEnd)}</dd></div>
                            <div><dt>Cancellation date</dt><dd>{formatDate(subscription?.planCancelAt)}</dd></div>
                        </dl>
                    )}
                    <div className={styles.formField}>
                        <span>Stripe customer ID</span>
                        <div className={styles.copyRow}>
                            <code>{user.stripeCustomerId ?? 'Not linked'}</code>
                            <button className={styles.secondaryButton} type="button" disabled={!user.stripeCustomerId} onClick={() => void handleCopyStripeId()}>Copy</button>
                        </div>
                    </div>
                    {canManageTarget && user.stripeCustomerId ? (
                        <div className={styles.sectionActions}>
                            <button className={styles.secondaryButton} type="button" disabled={activeOperation !== null} onClick={() => void handleSyncSubscription()}>{activeOperation === 'sync' ? 'Syncing…' : 'Sync from Stripe'}</button>
                            <button className={styles.dangerButton} type="button" disabled={activeOperation !== null || !canCancelSubscription} onClick={() => setConfirmation('cancelSubscription')}>Cancel at period end</button>
                        </div>
                    ) : null}
                    {canLinkLegacySubscription ? (
                        <div className={styles.recoveryPanel}>
                            <div>
                                <strong>Link an existing Stripe subscription</strong>
                                <p>SuperAdministrator recovery for a paid legacy signup. Enter the exact subscription ID; the customer is derived and verified by the server.</p>
                            </div>
                            <form onSubmit={handlePreviewLegacySubscription}>
                                <label className={styles.formField}>
                                    Stripe subscription ID
                                    <input
                                        value={legacySubscriptionId}
                                        placeholder="sub_..."
                                        maxLength={200}
                                        autoComplete="off"
                                        onChange={(event) => {
                                            setLegacySubscriptionId(event.target.value);
                                            setLegacyLinkPreview(null);
                                        }}
                                    />
                                </label>
                                <div className={styles.formActions}>
                                    <button
                                        className={styles.secondaryButton}
                                        type="submit"
                                        disabled={activeOperation !== null || !legacySubscriptionId.trim()}
                                    >
                                        {activeOperation === 'previewLegacySubscription' ? 'Checking Stripe…' : 'Preview from Stripe'}
                                    </button>
                                </div>
                            </form>
                            {legacyLinkPreview ? (
                                <div className={styles.recoveryPreview}>
                                    {!legacyLinkPreview.emailMatches ? (
                                        <div className={styles.warningBanner} role="alert">
                                            Stripe email {legacyLinkPreview.stripeCustomerEmail ?? 'is not set'} does not match {user.email}. Confirm carefully before linking.
                                        </div>
                                    ) : null}
                                    <dl className={styles.factList}>
                                        <div><dt>Customer</dt><dd>{legacyLinkPreview.stripeCustomerId}</dd></div>
                                        <div><dt>Stripe email</dt><dd>{legacyLinkPreview.stripeCustomerEmail ?? 'Not set'}</dd></div>
                                        <div><dt>Status</dt><dd>{legacyLinkPreview.status}</dd></div>
                                        <div><dt>Price</dt><dd>{legacyLinkPreview.priceId}</dd></div>
                                        <div><dt>Renews / ends</dt><dd>{formatDate(legacyLinkPreview.currentPeriodEndUtc)}</dd></div>
                                    </dl>
                                    <button
                                        className={legacyLinkPreview.emailMatches ? styles.primaryButton : styles.dangerButton}
                                        type="button"
                                        disabled={activeOperation !== null}
                                        onClick={() => setConfirmation('linkLegacySubscription')}
                                    >
                                        Review and link
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </section>

                <section className={styles.workspaceSection}>
                    <h2>Viewing activity</h2>
                    <p className={styles.sectionLead}>A 90-day summary for support and engagement context.</p>
                    {activityError ? <div className={styles.errorBanner}>{activityError}</div> : activity ? (
                        <dl className={styles.factList}>
                            <div><dt>Videos watched</dt><dd>{activity.totalVideos}</dd></div>
                            <div><dt>Completed</dt><dd>{activity.completedVideos}</dd></div>
                            <div><dt>In progress</dt><dd>{activity.inProgressVideos}</dd></div>
                            <div><dt>Average progress</dt><dd>{activity.averageProgressPercent.toFixed(1)}%</dd></div>
                        </dl>
                    ) : <p className={styles.sectionLead}>Loading viewing activity…</p>}
                    <div className={styles.sectionActions}>
                        <button className={styles.secondaryButton} type="button" onClick={openWatchHistory}>View full watch history</button>
                    </div>
                </section>

                <section className={`${styles.workspaceSection} ${styles.workspaceSectionFull}`}>
                    <h2>Account security</h2>
                    <p className={styles.sectionLead}>Security operations are independent from profile, role, and subscription saving.</p>
                    <div className={styles.actionList}>
                        <div className={styles.actionItem}>
                            <div><strong>Password reset</strong><span>Send a new reset link to {user.email} and require it before the next login.</span></div>
                            <button className={styles.secondaryButton} type="button" disabled={!canManageTarget || activeOperation !== null} onClick={() => setConfirmation('reset')}>Send reset email</button>
                        </div>
                        <div className={styles.actionItem}>
                            <div><strong>Account lifecycle</strong><span>Accounts are disabled reversibly. Combat TV does not hard-delete users from this workspace.</span></div>
                            <span className={`${styles.statusBadge} ${user.enabled ? styles.statusSuccess : styles.statusDanger}`}>{user.enabled ? 'Enabled' : 'Disabled'}</span>
                        </div>
                    </div>
                </section>
            </div>

            {confirmationContent ? (
                <ConfirmationDialog
                    {...confirmationContent}
                    busy={activeOperation !== null}
                    onClose={closeConfirmation}
                />
            ) : null}
        </div>
    );
};

export default AdminUserDetail;
